import { useCallback, useEffect, useState } from 'react';
import { ResourceActionPill } from '../components/ResourceActionPill';
import {
  PageHeader,
  PageRouteScaffold,
  DataTable,
  Button,
  Input,
  InlineError,
  openConfirmDelete,
  Stack,
  Group,
  Text,
  Drawer,
  Modal,
} from '@wapharma/design-system';
import { apiPost, apiPatch, apiDelete } from '../api';
import { apiGetListSimple } from '../apiList';
import { getToken } from '../auth';
import { useStaffRoles } from '../hooks/useStaffRoles';
import { useToast, type ToastTone, type ShowToastOptions } from '../contexts/ToastContext';
import { getDisplayError } from '../apiErrors';
import { formatMoneyFr } from '../utils/money';
import { HeaderPrimaryButton } from '../components/HeaderPrimaryButton';
import {
  DrawerWizardStepCard,
  DrawerWizardHeaderProgress,
  DrawerWizardBody,
  DrawerWizardNavFooter,
} from '../components/DrawerWizardStepCard.js';
import { AsyncActionButton } from '../components/AsyncActionButton';
import { useClientTablePagination } from '../hooks/useClientTablePagination';

const SHIP_CREATE_LABELS = ['Nom', 'Tarif', 'Description'] as const;
const PAY_CREATE_LABELS = ['Nom', 'Surcharge', 'Description'] as const;

type ShowToastFn = (message: string, toneOrOptions?: ToastTone | ShowToastOptions) => void;

type ShippingRow = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  price: unknown;
  active: boolean;
};

type PaymentRow = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  surcharge: unknown;
  active: boolean;
};

function numVal(v: unknown): number {
  if (typeof v === 'number') return v;
  return Number(String(v ?? '0').replace(',', '.')) || 0;
}

function fmtDhs(v: unknown): string {
  return formatMoneyFr(v, { suffix: 'Dhs' });
}

function slugify(s: string): string {
  return s.trim().toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '');
}

export type FulfillmentSettingsMode = 'shipping' | 'payment';

export function FulfillmentSettingsPage({
  mode = 'shipping',
  embedded = false,
  onChanged,
}: {
  mode?: FulfillmentSettingsMode;
  embedded?: boolean;
  onChanged?: () => void;
}) {
  const { canConfigureCommerce } = useStaffRoles();
  const token = getToken();
  const { showToast } = useToast();
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    setCreateOpen(false);
  }, [mode]);

  const canManage = canConfigureCommerce;
  const title = mode === 'shipping' ? 'Modes de livraison' : 'Modes de paiement';
  const createLabel = mode === 'shipping' ? 'Nouveau mode de livraison' : 'Nouveau mode de paiement';

  if (!canManage) {
    if (embedded) {
      return <Text c="dimmed">Accès réservé aux administrateurs.</Text>;
    }
    return (
      <PageRouteScaffold
        band={<PageHeader title={title} breadcrumbs={[{ label: 'Accueil', href: '/' }, { label: 'Paramètres' }]} variant="dock" />}
      >
        <Text c="dimmed">Accès réservé aux administrateurs.</Text>
      </PageRouteScaffold>
    );
  }

  const body = (
    <>
      {embedded ? (
        <Group justify="space-between" align="center" wrap="wrap" style={{ marginBottom: 'var(--wp-space-12)' }}>
          <Text size="sm" c="dimmed" style={{ flex: '1 1 12rem' }}>
            La boutique affiche les modes actifs tels quels.
          </Text>
          <HeaderPrimaryButton label={createLabel} onClick={() => setCreateOpen(true)} />
        </Group>
      ) : (
        <Text size="sm" c="dimmed" style={{ marginBottom: 'var(--wp-space-12)' }}>
          La boutique affiche les modes actifs tels quels (livraison standard / express / retrait, paiement CB /
          virement / COD).
        </Text>
      )}
      {mode === 'shipping' ? (
        <ShippingTab
          token={token}
          showToast={showToast}
          createOpen={createOpen}
          onCloseCreate={() => setCreateOpen(false)}
          onChanged={onChanged}
        />
      ) : (
        <PaymentTab
          token={token}
          showToast={showToast}
          createOpen={createOpen}
          onCloseCreate={() => setCreateOpen(false)}
          onChanged={onChanged}
        />
      )}
    </>
  );

  if (embedded) return body;

  return (
    <PageRouteScaffold
      band={
        <PageHeader
          title={title}
          breadcrumbs={[
            { label: 'Accueil', href: '/' },
            { label: 'Paramètres' },
          ]}
          variant="dock"
          end={
            <HeaderPrimaryButton label={createLabel} onClick={() => setCreateOpen(true)} />
          }
        />
      }
    >
      {body}
    </PageRouteScaffold>
  );
}

/* ── Shipping Tab ─────────────────────────────────────── */

function ShippingTab({
  token,
  showToast,
  createOpen,
  onCloseCreate,
  onChanged,
}: {
  token: string | null;
  showToast: ShowToastFn;
  createOpen: boolean;
  onCloseCreate: () => void;
  onChanged?: () => void;
}) {
  const [rows, setRows] = useState<ShippingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const [createStep, setCreateStep] = useState(0);
  const [createErr, setCreateErr] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [newDesc, setNewDesc] = useState('');

  const [editRow, setEditRow] = useState<ShippingRow | null>(null);
  const [editForm, setEditForm] = useState({ name: '', slug: '', description: '', price: '' });

  const { pagination: shippingPagination } = useClientTablePagination(25, [rows]);

  const load = useCallback(() => {
    if (!token) return;
    setErr(null);
    setLoading(true);
    void apiGetListSimple<ShippingRow>('/fulfillment/shipping-methods', token)
      .then(setRows)
      .catch((e) => setErr(getDisplayError(e)))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const closeCreate = () => { onCloseCreate(); setCreateStep(0); setCreateErr(null); setNewName(''); setNewPrice(''); setNewDesc(''); };

  const submitCreate = async () => {
    if (!token) return;
    setPending(true);
    setCreateErr(null);
    try {
      await apiPost('/fulfillment/shipping-methods', {
        name: newName.trim(),
        slug: slugify(newName),
        description: newDesc.trim() || undefined,
        price: parseFloat(newPrice) || 0,
      }, token);
      closeCreate();
      load();
      onChanged?.();
      showToast('Mode de livraison créé.', { tone: 'success' });
    } catch (e) {
      setCreateErr(getDisplayError(e));
    } finally {
      setPending(false);
    }
  };

  const openEdit = (r: ShippingRow) => {
    setEditRow(r);
    setEditForm({ name: r.name, slug: r.slug, description: r.description ?? '', price: String(numVal(r.price)) });
  };

  const closeEdit = () => { setEditRow(null); };

  const saveEdit = async () => {
    if (!token || !editRow) return;
    setPending(true);
    setErr(null);
    try {
      await apiPatch(`/fulfillment/shipping-methods/${editRow.id}`, {
        name: editForm.name.trim() || undefined,
        slug: editForm.slug.trim() || undefined,
        description: editForm.description.trim() || undefined,
        price: editForm.price ? parseFloat(editForm.price) : undefined,
      }, token);
      closeEdit();
      load();
      onChanged?.();
      showToast('Mode mis à jour.', { tone: 'success' });
    } catch (e) {
      setErr(getDisplayError(e));
    } finally {
      setPending(false);
    }
  };

  const toggleActive = (r: ShippingRow) => {
    if (!token) return;
    void apiPatch(`/fulfillment/shipping-methods/${r.id}`, { active: !r.active }, token)
      .then(() => {
        load();
        onChanged?.();
      })
      .catch((er) => showToast(getDisplayError(er), { tone: 'error' }));
  };

  const remove = (r: ShippingRow) => {
    if (!token) return;
    openConfirmDelete({
      title: 'Supprimer le mode de livraison',
      message: `Supprimer « ${r.name} » ? Les commandes existantes gardent leur référence.`,
      onConfirm: () =>
        apiDelete(`/fulfillment/shipping-methods/${r.id}`, token)
          .then(() => {
            load();
            onChanged?.();
            showToast('Supprimé.', { tone: 'success' });
          })
          .catch((er) => setErr(getDisplayError(er))),
    });
  };

  return (
    <Stack gap="md">
      {err ? <InlineError>{err}</InlineError> : null}
      <DataTable<ShippingRow>
        variant="list"
        pagination={shippingPagination}
        data={rows}
        rowKey={(r) => r.id}
        loading={loading}
        pinStartColumnKey="name"
        emptyMessage="Aucun mode de livraison."
        columns={[
          {
            key: 'name',
            label: 'Nom',
            render: (r) => (
              <Text size="md" fw={600} style={{ fontFamily: 'var(--wp-font-body)' }}>
                {r.name}
              </Text>
            ),
          },
          {
            key: 'slug',
            label: 'Slug',
            render: (r) => (
              <Text size="xs" c="dimmed" style={{ fontFamily: 'JetBrains Mono, var(--wp-font-mono)' }}>
                {r.slug}
              </Text>
            ),
          },
          {
            key: 'price',
            label: 'Prix',
            render: (r) => (
              <Text size="md" style={{ fontFamily: 'JetBrains Mono, var(--wp-font-mono)' }}>
                {fmtDhs(r.price)}
              </Text>
            ),
          },
        ]}
        rowLeadingActions={(r) => (
          <ResourceActionPill
            label={r.name}
            active={r.active}
            onEdit={() => openEdit(r)}
            onToggleActive={() => toggleActive(r)}
            onDelete={() => remove(r)}
          />
        )}
      />

      {/* ── Create Drawer ── */}
      <Drawer
        opened={createOpen}
        onClose={closeCreate}
        title="Nouveau mode de livraison"
        placement="top"
        headerBottom={
          <DrawerWizardHeaderProgress
            value={createStep + 1}
            max={3}
            label={`Étape ${createStep + 1} sur 3 — ${SHIP_CREATE_LABELS[createStep]}`}
          />
        }
        footer={
          <DrawerWizardNavFooter
            backLabel={createStep === 0 ? 'Annuler' : 'Précédent'}
            onBack={createStep === 0 ? closeCreate : () => setCreateStep((s) => s - 1)}
            backDisabled={pending}
          >
            {createStep < 2 ? (
              <Button
                type="button"
                variant="primary"
                disabled={pending || (createStep === 0 && !newName.trim())}
                onClick={() => {
                  if (createStep === 0 && !newName.trim()) {
                    setCreateErr('Nom requis.');
                    return;
                  }
                  setCreateErr(null);
                  setCreateStep((s) => s + 1);
                }}
              >
                Continuer
              </Button>
            ) : (
              <AsyncActionButton
                type="button"
                variant="primary"
                onAction={submitCreate}
                disabled={pending}
                pendingLabel="Création…"
              >
                Créer le mode
              </AsyncActionButton>
            )}
          </DrawerWizardNavFooter>
        }
      >
        <DrawerWizardBody>
          <Stack gap="sm">
            {createErr ? <InlineError>{createErr}</InlineError> : null}

            {createStep === 0 ? (
              <DrawerWizardStepCard title="Comment nommer ce mode de livraison ?">
                <Input aria-label="Nom" value={newName} onChange={(e) => setNewName(e.currentTarget.value)} autoFocus />
              </DrawerWizardStepCard>
            ) : createStep === 1 ? (
              <DrawerWizardStepCard title="Quel est le tarif de livraison ?">
                <Input aria-label="Prix (MAD)" type="number" step="0.01" min="0" value={newPrice} onChange={(e) => setNewPrice(e.currentTarget.value)} autoFocus />
              </DrawerWizardStepCard>
            ) : (
              <DrawerWizardStepCard title="Ajouter une description ? (optionnel)">
                <Input aria-label="Description" value={newDesc} onChange={(e) => setNewDesc(e.currentTarget.value)} autoFocus />
              </DrawerWizardStepCard>
            )}
          </Stack>
        </DrawerWizardBody>
      </Drawer>

      {/* ── Edit Modal ── */}
      <Modal opened={!!editRow} onClose={closeEdit} title={`Modifier : ${editRow?.name ?? ''}`}>
        <Stack gap="sm">
          <Input label="Nom" value={editForm.name} onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))} />
          <Input label="Slug" value={editForm.slug} onChange={(e) => setEditForm((p) => ({ ...p, slug: e.target.value }))} />
          <Input label="Prix (MAD)" type="number" step="0.01" min="0" value={editForm.price} onChange={(e) => setEditForm((p) => ({ ...p, price: e.target.value }))} />
          <Input label="Description" value={editForm.description} onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))} />
          {err ? <InlineError>{err}</InlineError> : null}
          <Group gap="sm" justify="flex-end">
            <Button type="button" variant="ghost" onClick={closeEdit}>Annuler</Button>
            <Button type="button" disabled={pending} onClick={() => void saveEdit()}>{pending ? 'Enregistrement…' : 'Enregistrer'}</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

/* ── Payment Tab ──────────────────────────────────────── */

function PaymentTab({
  token,
  showToast,
  createOpen,
  onCloseCreate,
  onChanged,
}: {
  token: string | null;
  showToast: ShowToastFn;
  createOpen: boolean;
  onCloseCreate: () => void;
  onChanged?: () => void;
}) {
  const [rows, setRows] = useState<PaymentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const [createStep, setCreateStep] = useState(0);
  const [createErr, setCreateErr] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newSurcharge, setNewSurcharge] = useState('');
  const [newDesc, setNewDesc] = useState('');

  const [editRow, setEditRow] = useState<PaymentRow | null>(null);
  const [editForm, setEditForm] = useState({ name: '', slug: '', description: '', surcharge: '' });

  const { pagination: paymentPagination } = useClientTablePagination(25, [rows]);

  const load = useCallback(() => {
    if (!token) return;
    setErr(null);
    setLoading(true);
    void apiGetListSimple<PaymentRow>('/fulfillment/payment-methods', token)
      .then(setRows)
      .catch((e) => setErr(getDisplayError(e)))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const closeCreate = () => { onCloseCreate(); setCreateStep(0); setCreateErr(null); setNewName(''); setNewSurcharge(''); setNewDesc(''); };

  const submitCreate = async () => {
    if (!token) return;
    setPending(true);
    setCreateErr(null);
    try {
      await apiPost('/fulfillment/payment-methods', {
        name: newName.trim(),
        slug: slugify(newName),
        description: newDesc.trim() || undefined,
        surcharge: parseFloat(newSurcharge) || 0,
      }, token);
      closeCreate();
      load();
      onChanged?.();
      showToast('Mode de paiement créé.', { tone: 'success' });
    } catch (e) {
      setCreateErr(getDisplayError(e));
    } finally {
      setPending(false);
    }
  };

  const openEdit = (r: PaymentRow) => {
    setEditRow(r);
    setEditForm({ name: r.name, slug: r.slug, description: r.description ?? '', surcharge: String(numVal(r.surcharge)) });
  };

  const closeEdit = () => { setEditRow(null); };

  const saveEdit = async () => {
    if (!token || !editRow) return;
    setPending(true);
    setErr(null);
    try {
      await apiPatch(`/fulfillment/payment-methods/${editRow.id}`, {
        name: editForm.name.trim() || undefined,
        slug: editForm.slug.trim() || undefined,
        description: editForm.description.trim() || undefined,
        surcharge: editForm.surcharge ? parseFloat(editForm.surcharge) : undefined,
      }, token);
      closeEdit();
      load();
      onChanged?.();
      showToast('Mode mis à jour.', { tone: 'success' });
    } catch (e) {
      setErr(getDisplayError(e));
    } finally {
      setPending(false);
    }
  };

  const toggleActive = (r: PaymentRow) => {
    if (!token) return;
    void apiPatch(`/fulfillment/payment-methods/${r.id}`, { active: !r.active }, token)
      .then(() => {
        load();
        onChanged?.();
      })
      .catch((er) => showToast(getDisplayError(er), { tone: 'error' }));
  };

  const remove = (r: PaymentRow) => {
    if (!token) return;
    openConfirmDelete({
      title: 'Supprimer le mode de paiement',
      message: `Supprimer « ${r.name} » ? Les commandes existantes gardent leur référence.`,
      onConfirm: () =>
        apiDelete(`/fulfillment/payment-methods/${r.id}`, token)
          .then(() => {
            load();
            onChanged?.();
            showToast('Supprimé.', { tone: 'success' });
          })
          .catch((er) => setErr(getDisplayError(er))),
    });
  };

  return (
    <Stack gap="md">
      {err ? <InlineError>{err}</InlineError> : null}
      <DataTable<PaymentRow>
        variant="list"
        pagination={paymentPagination}
        data={rows}
        rowKey={(r) => r.id}
        loading={loading}
        pinStartColumnKey="name"
        emptyMessage="Aucun mode de paiement."
        columns={[
          {
            key: 'name',
            label: 'Nom',
            render: (r) => (
              <Text size="md" fw={600} style={{ fontFamily: 'var(--wp-font-body)' }}>
                {r.name}
              </Text>
            ),
          },
          {
            key: 'slug',
            label: 'Slug',
            render: (r) => (
              <Text size="xs" c="dimmed" style={{ fontFamily: 'JetBrains Mono, var(--wp-font-mono)' }}>
                {r.slug}
              </Text>
            ),
          },
          {
            key: 'surcharge',
            label: 'Surcharge',
            render: (r) => numVal(r.surcharge) > 0
              ? <Text size="md" style={{ fontFamily: 'JetBrains Mono, var(--wp-font-mono)' }}>{fmtDhs(r.surcharge)}</Text>
              : <Text c="dimmed" size="sm">—</Text>,
          },
        ]}
        rowLeadingActions={(r) => (
          <ResourceActionPill
            label={r.name}
            active={r.active}
            onEdit={() => openEdit(r)}
            onToggleActive={() => toggleActive(r)}
            onDelete={() => remove(r)}
          />
        )}
      />

      {/* ── Create Drawer ── */}
      <Drawer
        opened={createOpen}
        onClose={closeCreate}
        title="Nouveau mode de paiement"
        placement="top"
        headerBottom={
          <DrawerWizardHeaderProgress
            value={createStep + 1}
            max={3}
            label={`Étape ${createStep + 1} sur 3 — ${PAY_CREATE_LABELS[createStep]}`}
          />
        }
        footer={
          <DrawerWizardNavFooter
            backLabel={createStep === 0 ? 'Annuler' : 'Précédent'}
            onBack={createStep === 0 ? closeCreate : () => setCreateStep((s) => s - 1)}
            backDisabled={pending}
          >
            {createStep < 2 ? (
              <Button
                type="button"
                variant="primary"
                disabled={pending || (createStep === 0 && !newName.trim())}
                onClick={() => {
                  if (createStep === 0 && !newName.trim()) {
                    setCreateErr('Nom requis.');
                    return;
                  }
                  setCreateErr(null);
                  setCreateStep((s) => s + 1);
                }}
              >
                Continuer
              </Button>
            ) : (
              <AsyncActionButton
                type="button"
                variant="primary"
                onAction={submitCreate}
                disabled={pending}
                pendingLabel="Création…"
              >
                Créer le mode
              </AsyncActionButton>
            )}
          </DrawerWizardNavFooter>
        }
      >
        <DrawerWizardBody>
          <Stack gap="sm">
            {createErr ? <InlineError>{createErr}</InlineError> : null}

            {createStep === 0 ? (
              <DrawerWizardStepCard title="Comment nommer ce mode de paiement ?">
                <Input aria-label="Nom" value={newName} onChange={(e) => setNewName(e.currentTarget.value)} autoFocus />
              </DrawerWizardStepCard>
            ) : createStep === 1 ? (
              <DrawerWizardStepCard title="Y a-t-il une surcharge pour ce mode ?">
                <Input aria-label="Surcharge (MAD)" type="number" step="0.01" min="0" value={newSurcharge} onChange={(e) => setNewSurcharge(e.currentTarget.value)} autoFocus />
              </DrawerWizardStepCard>
            ) : (
              <DrawerWizardStepCard title="Ajouter une description ? (optionnel)">
                <Input aria-label="Description" value={newDesc} onChange={(e) => setNewDesc(e.currentTarget.value)} autoFocus />
              </DrawerWizardStepCard>
            )}
          </Stack>
        </DrawerWizardBody>
      </Drawer>

      {/* ── Edit Modal ── */}
      <Modal opened={!!editRow} onClose={closeEdit} title={`Modifier : ${editRow?.name ?? ''}`}>
        <Stack gap="sm">
          <Input label="Nom" value={editForm.name} onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))} />
          <Input label="Slug" value={editForm.slug} onChange={(e) => setEditForm((p) => ({ ...p, slug: e.target.value }))} />
          <Input label="Surcharge (MAD)" type="number" step="0.01" min="0" value={editForm.surcharge} onChange={(e) => setEditForm((p) => ({ ...p, surcharge: e.target.value }))} />
          <Input label="Description" value={editForm.description} onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))} />
          {err ? <InlineError>{err}</InlineError> : null}
          <Group gap="sm" justify="flex-end">
            <Button type="button" variant="ghost" onClick={closeEdit}>Annuler</Button>
            <Button type="button" disabled={pending} onClick={() => void saveEdit()}>{pending ? 'Enregistrement…' : 'Enregistrer'}</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
